import React from 'react';
import { WriteOffStatus } from '@repo/types';

/**
 * One write-off an edit form can mutate, described by the caller. The helper is the BROADEST
 * case: every status/form supplies only the impacts it actually has and the helper derives the
 * consequence per row, so a caller "gets the case it wants":
 *  - NO_APPEAL / DEADLINE_EXPIRED pass cuts + the discount triplet + (DE) a Write-Off Remaining.
 *  - The wide TBA / AAS / AS-Backlog form passes only the discount triplet (cuts are appeal
 *    fields there, not write-off rows).
 *  - APPEAL_APPROVED passes the approved-cuts row + the approval Prompt Payment row.
 *  - AS-Subsequent passes none (no write-off ops) → no banner.
 *
 * The flags mirror the backend reconcile so the warning never lies about what `Save` will do.
 */
export interface WriteOffEditImpact {
  /** Friendly name shown in the warning, e.g. "Prompt Payment". */
  label: string;
  /** Did the form field driving this write-off change vs. the saved value? */
  changed: boolean;
  /** Live form value for this write-off (0 means the clerk cleared it). */
  newAmount: number;
  /** Current status of the existing row; undefined when no row exists yet. */
  status?: WriteOffStatus;
  /** False when no backing row exists (nothing to reconcile). */
  hasRow: boolean;
  /**
   * On a non-zero change the BE demotes an accepted/escalated row back to Proposed for
   * re-approval (true for CUTS_APPLIED). Discounts stay Approved, so they leave this false.
   */
  demoteOnChange?: boolean;
  /**
   * On a non-zero change of an ARCHIVED row the BE re-approves it (ARCHIVED → APPROVED).
   * True for the discount triplet.
   */
  reapproveIfArchived?: boolean;
  /** A zeroed field hard-deletes the row (true wherever the BE deletes on amount → 0). */
  deleteOnZero?: boolean;
}

// Statuses a row can sit in that a non-zero edit demotes back to Proposed for re-approval.
const DEMOTABLE_STATUSES: ReadonlyArray<WriteOffStatus> = [
  WriteOffStatus.APPROVED,
  WriteOffStatus.ESCALATED,
  WriteOffStatus.ARCHIVED,
];

/**
 * Group the status-changing consequences of an edit into ≤ 4 short lines, colour-coded by
 * outcome (amber → Proposed, green → Approved, red → Remove / Clio). Pure; returns [] when the
 * edit has no status consequence (e.g. only a non-zero discount change, which stays Approved).
 */
export function buildEditReconcileWarningLines(
  impacts: ReadonlyArray<WriteOffEditImpact>
): React.ReactNode[] {
  const toProposed: string[] = [];
  const toApproved: string[] = [];
  const removed: string[] = [];
  const clio = new Set<string>();

  for (const w of impacts) {
    if (!w.changed || !w.hasRow) continue;

    // Touching an ARCHIVED row always needs a Clio credit-note adjustment (already credited there).
    if (w.status === WriteOffStatus.ARCHIVED) clio.add(w.label);

    if (w.newAmount === 0) {
      if (w.deleteOnZero) removed.push(w.label);
      continue;
    }

    // newAmount > 0
    if (w.demoteOnChange && w.status && DEMOTABLE_STATUSES.includes(w.status)) {
      toProposed.push(w.label);
    } else if (w.reapproveIfArchived && w.status === WriteOffStatus.ARCHIVED) {
      toApproved.push(w.label);
    }
    // else: updated in place, stays Approved → nothing to warn about.
  }

  const lines: React.ReactNode[] = [];
  if (toProposed.length) {
    lines.push(
      <>
        Send <strong>{toProposed.join(', ')}</strong> back to{' '}
        <span className="font-bold text-amber-700 dark:text-amber-300">Proposed</span>
      </>
    );
  }
  if (toApproved.length) {
    lines.push(
      <>
        Return <strong>{toApproved.join(', ')}</strong> to{' '}
        <span className="font-bold text-emerald-600 dark:text-emerald-400">Approved</span>
      </>
    );
  }
  if (removed.length) {
    lines.push(
      <>
        <span className="font-bold text-red-600 dark:text-red-400">Remove</span>{' '}
        <strong>{removed.join(', ')}</strong>
      </>
    );
  }
  if (clio.size) {
    lines.push(
      <>
        Require a{' '}
        <span className="font-bold text-red-600 dark:text-red-400">
          Clio credit-note adjustment
        </span>{' '}
        for <strong>{Array.from(clio).join(', ')}</strong>
      </>
    );
  }
  return lines;
}

/**
 * Shared "Saving Changes will:" amber banner. Renders nothing when there are no lines, so callers
 * can drop it in unconditionally.
 */
export function EditReconcileWarningBanner({
  lines,
  className = 'mt-4',
}: {
  lines: React.ReactNode[];
  className?: string;
}) {
  if (!lines.length) return null;
  return (
    <div
      role="alert"
      className={`${className} p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700`}
    >
      <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">Saving Changes will:</p>
      <ul className="mt-1 list-disc pl-4 space-y-0.5 text-xs text-amber-800 dark:text-amber-200 leading-snug">
        {lines.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
    </div>
  );
}
